import { useCallback, useReducer, useState } from 'react';
import { ECrmActivityType } from 'services/CrmApi/types/CrmActivityTypes';
import { getReferencedActivityIdFromUrl, removeActivityIdFromUrl } from '../helpers';
import { ESortOrder } from 'store/common/types';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

export interface IActivityFiltersState {
  flags: {
    [ECrmActivityType.CALL]: boolean;
    [ECrmActivityType.MEETING]: boolean;
    [ECrmActivityType.NOTE]: boolean;
    [ECrmActivityType.TASK]: boolean;
  };
  filters?: {
    dateRange?: {
      startDate?: string;
      endDate?: string;
    };
    createdById?: string;
    searchTerm?: string;
  };
  pagination: {
    offset: number;
    limit: number;
  };
  sort: {
    key: 'date';
    order: ESortOrder;
  }
  cursor?: {
    limit: string; // uuid of an activity
  };
}

export type ActivityFlagsActionType =
  | 'set_calls_filter'
  | 'set_meetings_filter'
  | 'set_notes_filter'
  | 'set_tasks_filter';

export type ActivityFiltersActionType = 'set_start_date' | 'set_end_date' | 'set_created_by_id' | 'set_search_term';

export type ActivityPaginationActionType = 'set_offset' | 'set_limit';

export type ActivityCursorActionType = 'set_cursor';

export type ActivitySortActionType = 'set_order_by';

export interface IActivityFlagsAction {
  type: ActivityFlagsActionType;
  value: boolean;
}

export interface IActivityFiltersAction {
  type: ActivityFiltersActionType;
  value: string | undefined;
}

export interface IActivityPaginationAction {
  type: ActivityPaginationActionType;
  value: number;
}

export interface IActivityCursorAction {
  type: ActivityCursorActionType;
  value: string | undefined;
}

export interface IActivitySortAction {
  type: ActivitySortActionType;
  value: ESortOrder;
}

const reducer = (
  state: IActivityFiltersState,
  action: IActivityFlagsAction | IActivityPaginationAction | IActivityCursorAction | IActivityFiltersAction | IActivitySortAction
) => {
  switch (action.type) {
    case 'set_calls_filter':
      return { ...state, flags: { ...state.flags, [ECrmActivityType.CALL]: action.value } };
    case 'set_meetings_filter':
      return { ...state, flags: { ...state.flags, [ECrmActivityType.MEETING]: action.value } };
    case 'set_notes_filter':
      return { ...state, flags: { ...state.flags, [ECrmActivityType.NOTE]: action.value } };
    case 'set_tasks_filter':
      return { ...state, flags: { ...state.flags, [ECrmActivityType.TASK]: action.value } };
    case 'set_offset':
      return { ...state, pagination: { ...state.pagination, offset: action.value } };
    case 'set_limit':
      return { ...state, pagination: { ...state.pagination, limit: action.value } };
    case 'set_cursor':
      if (action.value) {
        return { ...state, cursor: { ...state.cursor, limit: action.value } };
      }
      return { ...state, cursor: undefined };
    case 'set_order_by':
      return { ...state, sort: { ...state.sort, order: action.value } };
    case 'set_start_date':
      return { ...state, filters: { ...state.filters, dateRange: { ...state.filters?.dateRange, startDate: action.value } } };
    case 'set_end_date':
      return { ...state, filters: { ...state.filters, dateRange: { ...state.filters?.dateRange, endDate: action.value } } };
    case 'set_created_by_id':
      return { ...state, filters: { ...state.filters, createdById: action.value } };
    case 'set_search_term':
      return { ...state, filters: { ...state.filters, searchTerm: action.value } };
    default:
      throw Error('Unknown action.');
  }
};

const getInitialActivityFiltersState = (isCrmTmsEnabled: boolean): IActivityFiltersState => {
  const initialState: IActivityFiltersState = {
    flags: {
      [ECrmActivityType.CALL]: true,
      [ECrmActivityType.MEETING]: true,
      [ECrmActivityType.NOTE]: true,
      [ECrmActivityType.TASK]: isCrmTmsEnabled,
    },
    pagination: {
      offset: 0,
      limit: 25,
    },
    sort: {
      key: 'date',
      order: ESortOrder.DESC,
    },
  };

  const referencedActivityId = getReferencedActivityIdFromUrl();
  if (referencedActivityId) {
    initialState.cursor = { limit: referencedActivityId };
  }

  return initialState;
};

export const useActivityFilters = (isCrmTmsEnabled: boolean = false) => {
  const [activityFilters, dispatchActivityFilters] = useReducer(reducer, getInitialActivityFiltersState(isCrmTmsEnabled));
  const [highlightedActivityId, setHighlightedActivityId] = useState<string | null>(null);

  const resetHighlightedActivity = useCallback(() => {
    dispatchActivityFilters({ type: 'set_cursor', value: undefined });
    removeActivityIdFromUrl();
    setHighlightedActivityId(null);
  }, [dispatchActivityFilters]);

  const handleSortOrderChange = useCallback((newSortOrder: ESortOrder) => {
    dispatchActivityFilters({ type: 'set_order_by', value: newSortOrder });
    removeActivityIdFromUrl();
    setHighlightedActivityId(null);
  }, [dispatchActivityFilters]);

  return { activityFilters, dispatchActivityFilters, resetHighlightedActivity, highlightedActivityId, setHighlightedActivityId, handleSortOrderChange };
};
